import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorldCanvas, WorldCanvasHandle, Viewport } from '../components/WorldCanvas/WorldCanvas';
import { WebSocketClient, WsEvent, WsStatusInfo, getWsBase } from '../api/websocket';
import { ConnectionDot } from '../components/ConnectionDot/ConnectionDot';
import { Point, samePoint } from '../types/point';
import { useAuth } from '../hooks/useAuth';
import { GridOverlay } from '../components/GridOverlay/GridOverlay';
import { SocialLinks } from '../components/SocialLinks/SocialLinks';
import { AppNav } from '../components/AppNav/AppNav';
import { toastSuccess } from '../utils/toast';
import { useHoldRepeat } from '../hooks/useHoldRepeat';
import { usePlayAction } from '../hooks/usePlayAction';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { AdminPoint, getAdminPointAt } from '../api/admin';
import { useSpeechBubbles } from '../hooks/useSpeechBubbles';
import { SpeechBubbles } from '../components/SpeechBubbles';


export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [points,     setPoints]     = useState<Point[]>([]);
  const [viewport,   setViewport]   = useState<Viewport>(() => ({
    x:    parseFloat(searchParams.get('x')    ?? '0') || 0,
    y:    parseFloat(searchParams.get('y')    ?? '0') || 0,
    zoom: parseFloat(searchParams.get('zoom') ?? '1') || 1,
  }));
  const [coordInput,  setCoordInput]  = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [showGrid,    setShowGrid]    = useState(() => localStorage.getItem('showGrid') === 'true');
  const [windowSize, setWindowSize] = useState({
    width : window.innerWidth,
    height: window.innerHeight,
  });

  const [wsStatus, setWsStatus] = useState<WsStatusInfo | null>(null);
  const wsRef          = useRef<WebSocketClient | null>(null);
  const worldCanvasRef = useRef<WorldCanvasHandle>(null);
  const vpRef          = useRef(viewport);
  vpRef.current        = viewport;
  const pointsRef      = useRef(points);
  pointsRef.current    = points;
  const windowSizeRef  = useRef(windowSize);
  windowSizeRef.current = windowSize;
  const resubTimer     = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Points moved since the last subscribe — their positions are more current
  // than any in-flight init snapshot; skip position overwrite for them.
  const movedSinceSubscribeRef = useRef<Set<number>>(new Set());
  // RAF batching for points_update — collapses multiple region updates per frame.
  const pendingMergeRef = useRef<Map<number, Point>>(new Map());
  // Position-only updates from individual 'moved' events, batched the same way.
  const pendingMovedRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const mergeRafRef     = useRef<number | null>(null);

  // Apply both accumulators in one setPoints call, at most once per frame.
  const scheduleMerge = useCallback(() => {
    if (mergeRafRef.current !== null) return;
    mergeRafRef.current = requestAnimationFrame(() => {
      mergeRafRef.current = null;
      const updates = Array.from(pendingMergeRef.current.values());
      pendingMergeRef.current.clear();
      const moves = new Map(pendingMovedRef.current);
      pendingMovedRef.current.clear();
      if (updates.length === 0 && moves.size === 0) return;
      setPoints(prev => {
        const map = new Map(prev.map((p: Point) => [p.id, p]));
        let changed = false;
        updates.forEach(p => {
          const existing = map.get(p.id);
          if (existing && samePoint(existing, p)) return;
          map.set(p.id, p);
          changed = true;
        });
        moves.forEach(({ x, y }, id) => {
          const existing = map.get(id);
          if (!existing || (existing.x === x && existing.y === y)) return;
          map.set(id, { ...existing, x, y });
          changed = true;
        });
        return changed ? Array.from(map.values()) : prev;
      });
    });
  }, []);

  // ── Window resize ──────────────────────────────────────────────
  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Zone payment redirect-back ─────────────────────────────────
  useEffect(() => {
    if (searchParams.get('payment') === 'zone_success') {
      toastSuccess(t('zone.paymentSuccess'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  // ── Send WS subscribe for the visible world area ───────────────
  const sendSubscribe = useCallback((vp: Viewport) => {
    const { width: w, height: h } = windowSizeRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    movedSinceSubscribeRef.current.clear();
    wsRef.current?.send({
      type: 'subscribe',
      area: {
        x_min: Math.round(vp.x) - halfW,
        y_min: Math.round(vp.y) - halfH,
        x_max: Math.round(vp.x) + halfW,
        y_max: Math.round(vp.y) + halfH,
      },
    });
  }, []);

  // ── Open WebSocket as guest observer ────────────────────────────
  useEffect(() => {
    const vp = vpRef.current;
    const { width: w, height: h } = windowSizeRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    const initArea = {
      x_min: Math.round(vp.x) - halfW,
      y_min: Math.round(vp.y) - halfH,
      x_max: Math.round(vp.x) + halfW,
      y_max: Math.round(vp.y) + halfH,
    };

    const wsBase = getWsBase();
    const url    = `${wsBase}?token=guest&area=${initArea.x_min},${initArea.y_min},${initArea.x_max},${initArea.y_max}`;

    const client = new WebSocketClient(url, (event: WsEvent) => {
      if (event.type === 'init') {
        const all = Array.isArray(event.points) ? event.points : [];
        const moved = movedSinceSubscribeRef.current;
        setPoints(prev => {
          const map = new Map(prev.map((p: Point) => [p.id, p]));
          // A re-subscribe usually returns exactly what we already have. Keeping
          // the old array then costs nothing downstream: no re-render, no
          // renderer rescan of a viewport that may hold tens of thousands of points.
          let changed = false;
          all.forEach((p: Point) => {
            const existing = map.get(p.id);
            if (!existing) {
              map.set(p.id, p);
              changed = true;
            } else if (moved.has(p.id)) {
              if (existing.color !== p.color) {
                map.set(p.id, { ...existing, color: p.color });
                changed = true;
              }
            } else if (!samePoint(existing, p)) {
              map.set(p.id, p);
              changed = true;
            }
          });
          return changed ? Array.from(map.values()) : prev;
        });
        // No sendSubscribe here — the connection was opened with the correct area already.
      } else if (event.type === 'points_update') {
        const incoming: Point[] = Array.isArray(event.points) ? event.points : [];
        incoming.forEach(p => pendingMergeRef.current.set(p.id, p));
        scheduleMerge();
      } else if (event.type === 'moved') {
        movedSinceSubscribeRef.current.add(event.pointId);
        // Batched with points_update: one array rebuild per frame, not per event.
        pendingMovedRef.current.set(event.pointId, { x: event.newX, y: event.newY });
        scheduleMerge();
      } else if (event.type === 'point_removed') {
        const removedId = event.point_id;
        setPoints(prev => prev.filter(p => p.id !== removedId));
      } else if (event.type === 'said') {
        handleSaidEvent(event.point_id, event.text, event.style);
      } else if (event.type === 'jumped') {
        worldCanvasRef.current?.triggerJump(event.point_id);
      }
    }, setWsStatus);
    wsRef.current = client;
    return () => {
      client.close();
      wsRef.current = null;
      if (resubTimer.current) clearTimeout(resubTimer.current);
      if (mergeRafRef.current !== null) {
        cancelAnimationFrame(mergeRafRef.current);
        mergeRafRef.current = null;
      }
      pendingMergeRef.current.clear();
      movedSinceSubscribeRef.current.clear();
      clearSpeeches();
    };
  }, []);

  // ── Viewport change (drag, wheel, buttons) ─────────────────────
  /**
   * Single handler for all viewport mutations.
   * Updates React state immediately and debounces a WS subscribe so the
   * observer always loads points in the area they're currently viewing.
   */
  const handleViewportChange = useCallback((newVp: Viewport) => {
    setViewport(newVp);

    if (resubTimer.current) clearTimeout(resubTimer.current);
    resubTimer.current = setTimeout(() => sendSubscribe(newVp), 250);
  }, [sendSubscribe]);

  // ── Button-driven navigation helpers ───────────────────────────
  const adjustZoom = (factor: number) => {
    const vp = vpRef.current;
    handleViewportChange({
      ...vp,
      zoom: Math.max(0.5, Math.min(1000, vp.zoom * factor)),
    });
  };

  /** Navigate to manually entered coordinates. */
  const handleGoTo = () => {
    const parts = coordInput.split(',').map(s => parseInt(s.trim(), 10));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      handleViewportChange({ ...vpRef.current, x: parts[0], y: parts[1] });
      setCoordInput('');
    }
  };

  const handleShare = useCallback(() => {
    const vp = vpRef.current;
    const url = `${window.location.origin}/?x=${Math.round(vp.x)}&y=${Math.round(vp.y)}&zoom=${vp.zoom.toFixed(1)}`;
    navigator.clipboard.writeText(url).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, []);

  const holdUp    = useHoldRepeat(useCallback(() => { const vp = vpRef.current; handleViewportChange({ ...vp, y: vp.y + 10 }); }, [handleViewportChange]));
  const holdDown  = useHoldRepeat(useCallback(() => { const vp = vpRef.current; handleViewportChange({ ...vp, y: vp.y - 10 }); }, [handleViewportChange]));
  const holdLeft  = useHoldRepeat(useCallback(() => { const vp = vpRef.current; handleViewportChange({ ...vp, x: vp.x - 10 }); }, [handleViewportChange]));
  const holdRight = useHoldRepeat(useCallback(() => { const vp = vpRef.current; handleViewportChange({ ...vp, x: vp.x + 10 }); }, [handleViewportChange]));

  const { speeches, handleSaidEvent, clearSpeeches } = useSpeechBubbles();
  const { play: handlePlay, loading: playLoading } = usePlayAction();

  // ── Admin: who left this pixel? (hover tooltip) ─────────────────
  // Admin-only on purpose: regular clients must never receive metadata about
  // foreign points. The lookup goes through GET /api/admin/points/at, owners
  // are cached by point id (ownership never changes), and the fetch is
  // debounced so sweeping the cursor across a mural doesn't strafe the API.
  const isAdmin = useSelector((s: RootState) => s.auth.isAdmin);
  const [ownerTip, setOwnerTip] = useState<{ x: number; y: number; point: AdminPoint | null } | null>(null);
  const ownerCacheRef = useRef<Map<number, AdminPoint>>(new Map());
  const ownerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ownerCellRef  = useRef('');

  const lookupOwner = useCallback((wx: number, wy: number, immediate: boolean) => {
    const cx = Math.round(wx);
    const cy = Math.round(wy);
    const key = `${cx}:${cy}`;
    if (key === ownerCellRef.current && !immediate) return;
    ownerCellRef.current = key;
    if (ownerTimerRef.current) {
      clearTimeout(ownerTimerRef.current);
      ownerTimerRef.current = null;
    }

    const local = pointsRef.current.find(p => p.x === cx && p.y === cy);
    if (!local) {
      setOwnerTip(null);
      return;
    }

    const cached = ownerCacheRef.current.get(local.id);
    if (cached) {
      setOwnerTip({ x: cx, y: cy, point: { ...cached, x: cx, y: cy } });
      return;
    }

    setOwnerTip({ x: cx, y: cy, point: null }); // ellipsis while loading
    const fetchOwner = () => {
      ownerTimerRef.current = null;
      getAdminPointAt(cx, cy)
        .then(res => {
          ownerCacheRef.current.set(res.data.point.id, res.data.point);
          if (ownerCellRef.current === key) setOwnerTip({ x: cx, y: cy, point: res.data.point });
        })
        .catch(() => {
          if (ownerCellRef.current === key) setOwnerTip(null);
        });
    };
    if (immediate) fetchOwner();                                   // tap: fetch right away
    else ownerTimerRef.current = setTimeout(fetchOwner, 150);      // hover: debounce sweeps
  }, []);

  const handleWorldHover = useCallback((wx: number, wy: number) => lookupOwner(wx, wy, false), [lookupOwner]);
  // Mobile has no hover — a tap on a pixel shows the same tooltip (tap elsewhere hides it).
  const handleWorldTap   = useCallback((wx: number, wy: number) => lookupOwner(wx, wy, true),  [lookupOwner]);

  // ── Promo capture hook (?promo=1) ──────────────────────────────
  /**
   * Exposes the camera to the promo-video recorder (video/capture.py), which
   * drives it frame by frame to get perfectly smooth cinematic moves, and hides
   * the HUD via body.promo-mode. Inert without the query param.
   */
  const promoMode = searchParams.get('promo') === '1';
  useEffect(() => {
    if (!promoMode) return;
    const w = window as any;
    w.__wpPromo = {
      setViewport: (x: number, y: number, zoom: number, subscribe = false) => {
        const vp = { x, y, zoom };
        setViewport(vp);
        if (subscribe) sendSubscribe(vp);
      },
      subscribe: () => sendSubscribe(vpRef.current),
      setGrid: (on: boolean) => setShowGrid(on),
      pointCount: () => pointsRef.current.length,
    };
    document.body.classList.add('promo-mode');
    return () => {
      delete w.__wpPromo;
      document.body.classList.remove('promo-mode');
    };
  }, [promoMode, sendSubscribe]);

  return (
    <div className="home-page">
      {/* Full-screen canvas — drag to pan, wheel to zoom */}
      <WorldCanvas
        ref={worldCanvasRef}
        points={points}
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        onViewportChange={handleViewportChange}
        onWorldHover={isAdmin ? handleWorldHover : undefined}
        onWorldClick={isAdmin ? handleWorldTap : undefined}
      />
      <GridOverlay
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        visible={showGrid}
      />

      {/* Admin-only: owner of the hovered pixel, anchored to its cell */}
      {isAdmin && ownerTip && (
        <div
          className="home-page__owner-tip"
          style={{
            left: (ownerTip.x - viewport.x) * viewport.zoom + windowSize.width / 2,
            top : -(ownerTip.y - viewport.y) * viewport.zoom + windowSize.height / 2 - viewport.zoom / 2 - 8,
          }}
        >
          {ownerTip.point ? (
            <>
              <div className="home-page__owner-tip-email">{ownerTip.point.user_email}</div>
              <div className="home-page__owner-tip-meta">
                <span className="home-page__owner-tip-swatch" style={{ background: ownerTip.point.color }} />
                {ownerTip.point.name} #{ownerTip.point.id} · ({ownerTip.x}, {ownerTip.y})
              </div>
              <div className="home-page__owner-tip-meta">
                {new Date(ownerTip.point.created_at).toLocaleDateString()}
                {' → '}
                {new Date(ownerTip.point.updated_at).toLocaleString()}
              </div>
            </>
          ) : (
            <div className="home-page__owner-tip-meta">…</div>
          )}
        </div>
      )}

      {/* Speech bubbles — same as GamePage */}
      <SpeechBubbles
        speeches={speeches}
        points={points}
        viewport={viewport}
        windowSize={windowSize}
      />

      {/* Social links — top left */}
      <div className="home-page__hud home-page__hud--top-left">
        <SocialLinks />
      </div>

      {/* Nav — top right (shared AppNav component) */}
      <div className="home-page__hud home-page__hud--top-right">
        <ConnectionDot statusInfo={wsStatus} />
        <AppNav currentPage="home" />
      </div>

      {/* Play button — bottom center, only for logged-in users */}
      {token && (
        <div className="home-page__hud home-page__hud--bottom-center">
          <button
            className="btn-transparent home-play-btn"
            onClick={handlePlay}
            disabled={playLoading}
          >
            {playLoading ? t('common.loading') : t('home.playBtn')}
          </button>
        </div>
      )}

      {/* Coords + share — bottom left */}
      <div className="home-page__hud home-page__hud--bottom-left">
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          <div className="btn-transparent" style={{ pointerEvents: 'none', opacity: 0.8 }}>
            X: <b>{Math.round(viewport.x)}</b> &nbsp; Y: <b>{Math.round(viewport.y)}</b>
          </div>
          <button className="btn-transparent" onClick={handleShare}>
            {shareCopied ? t('common.copied') : t('common.share')}
          </button>
        </div>
      </div>

      {/* Navigation controls — bottom right */}
      <div className="home-page__hud home-page__hud--bottom-right">
        {/* Coordinate jump */}
        <div className="hud-goto" style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          <input
            className="btn-transparent"
            style={{ width: '80px', textAlign: 'center', background: 'rgba(0,0,0,0.55)' }}
            placeholder={t('common.coordsPlaceholder')}
            value={coordInput}
            onChange={e => setCoordInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGoTo()}
          />
          <button className="btn-transparent" onClick={handleGoTo}>{t('common.go')}</button>
        </div>

        {/* D-pad */}
        <div className="hud-dpad">
          <button className="btn-transparent dpad-up"    {...holdUp}>↑</button>
          <button className="btn-transparent dpad-left"  {...holdLeft}>←</button>
          <button className="btn-transparent dpad-down"  {...holdDown}>↓</button>
          <button className="btn-transparent dpad-right" {...holdRight}>→</button>
        </div>

        {/* Zoom */}
        <div className="hud-zoom">
          <button className="btn-transparent" onClick={() => adjustZoom(1.2)}>+</button>
          <button className="btn-transparent" onClick={() => adjustZoom(1 / 1.2)}>−</button>
        </div>
        {/* Grid toggle */}
        <button
          className={`btn-transparent game-hud-grid-btn${showGrid ? ' game-hud-grid-btn--active' : ''}`}
          onClick={() => setShowGrid(v => { const next = !v; localStorage.setItem('showGrid', String(next)); return next; })}
        >
          {showGrid ? '▨' : '▦'}<span className="grid-btn-text">{' '}{showGrid ? t('game.gridOff') : t('game.gridOn')}</span>
        </button>
      </div>
    </div>
  );
};
